import React, { ChangeEvent, useContext, useEffect, useState } from 'react';
import { Day, DayValue } from 'react-modern-calendar-datepicker';
import 'react-modern-calendar-datepicker/lib/DatePicker.css';
import moment from 'moment';

import { UserContext } from '@/routes';
import { PetType } from './PetType';
import { Heading } from '@/components/Elements/Heading';
import PetWeightSelection from './PetWeightSelection';
import { PetWeight } from '@/features/payment/api/petWeights';
import { Customers, Pets } from '@/features/customers';
import { Button } from '@/components/Elements';
import { showNotification } from '@/utils/notificationUtils';
import { ReactComponent as Servicesicon4 } from '@/assets/img/add-icon-red.svg';
import { ReactComponent as DeleteIcon } from '@/assets/img/delete-icon-v2.svg';

import Utils from '@/utils/util';
import { DropDown } from '@/components/dropdown/DropDown';
import { WeightPriceConfiguration } from '@/features/services/components/PetWeightConfig';
import DatePickerWithPortal from '@/components/DatePicker/DatepickerWithPortal';
import { t } from 'i18next';
type Props = {
  colorList?: any;
  breedList?: any;
  data?: Pets;
  defaultPetType?: string;
  onAddNewPet: (pet: Pets | any) => void;
  hideAddNewPet?: boolean;
  selectedCustomer?: Customers;
  onDeleteNewPet: () => void;
  totalNewPets: number;
  selectedBookingView: string;
  availableSizeConfiguration?: WeightPriceConfiguration | undefined | null;
};
export const PetForm = ({
  data,
  colorList,
  hideAddNewPet,
  breedList,
  onAddNewPet,
  onDeleteNewPet,
  totalNewPets,
  selectedBookingView,
  availableSizeConfiguration,
  defaultPetType,
}: Props) => {
  const userContext = useContext(UserContext);
  const [showMorePetDetails, setShowMorePetDetails] = useState(false);
  const [pet, setPet] = useState<Pets>();

  useEffect(() => {
    if (data) {
      setPet(data);
    } else {
      setPet({
        name: '',
        id: '',
        breed: Utils.BREED_UNSPECIFIED,
        gender: '',
        dateOfBirth: '',
        notes: '',
        petsImageUrl: '',
        petColor: '',
        weight: '',
        petType: defaultPetType ? defaultPetType : '',
        petSize: '',
        petAge: '',
      });
    }
  }, [data]);

  const [day, setDay] = React.useState<Day>({
    day: moment().date(),
    month: moment().month() + 1,
    year: moment().year(),
  } as Day);

  const updatePetData = (petData: Pets | any) => {
    const newPet = { ...petData };
    if (selectedBookingView === 'grooming') {
      onAddNewPet(newPet);
    } else {
      setPet(newPet);
    }
  };
  const handleChangePetDetails = (event: ChangeEvent<HTMLInputElement> | any) => {
    const petData = { ...pet };
    const value = event.target.value ? event.target.value : '';
    if (event.target.id === 'name') {
      petData.name = value;
    } else if (event.target.id === 'weight') {
      petData.weight = value;
    } else if (event.target.id === 'notes') {
      petData.notes = value;
    } else if (event.target.id === 'date') {
      petData.dateOfBirth = value;
    } else if (event.target.id === 'petAge') {
      petData.petAge = value;
    }
    updatePetData(petData);
  };

  const onPetSizeChange = (petSize: PetWeight) => {
    if (pet) {
      const petData = { ...pet };
      petData.petSize = petSize?.key ? petSize?.key : '';
      updatePetData(petData);
    }
  };

  const onPetTypeChange = (petType: string) => {
    const petData = { ...pet };
    petData.petType = petType;
    updatePetData(petData);
  };
  const onGenderChange = (gender: string) => {
    const petData = { ...pet };
    petData.gender = gender;
    updatePetData(petData);
  };
  const onBreedChange = (breed: string) => {
    const petData = { ...pet };
    petData.breed = breed;
    updatePetData(petData);
  };
  const onColorChange = (color: string) => {
    const petData = { ...pet };
    petData.petColor = color;
    updatePetData(petData);
  };
  const onDOBChange = (dpData: any) => {
    const petData = { ...pet };
    petData.dateOfBirth = `${dpData?.month}/${dpData?.day}/${dpData?.year}`;
    updatePetData(petData);
  };
  const validate = () => {
    let errorMessage = undefined;
    if (pet) {
      const { name, petType, breed } = pet;
      if (!petType) {
        errorMessage = 'Please select type of the pet';
      } else if (!name || name.trim().length < 1) {
        errorMessage = 'Please enter pet name';
      } else if (!breed || breed.trim().length < 1) {
        errorMessage = 'Please select the pet breed';
      }
    }

    if (errorMessage) {
      showNotification({
        type: 'info',
        title: errorMessage,
      });
      return false;
    } else {
      return true;
    }
  };
  const addNewPet = () => {
    if (validate()) {
      onAddNewPet(pet);
      setPet({
        name: '',
        id: '',
        breed: Utils.BREED_UNSPECIFIED,
        gender: '',
        dateOfBirth: '',
        notes: '',
        petsImageUrl: '',
        petColor: '',
        weight: '',
        petType: defaultPetType ? defaultPetType : '',
        petSize: '',
        petAge: '',
      });
    }
  };
  return pet ? (
    <div className="">
      <>
        {' '}
        {userContext && userContext?.isLandscape ? (
          <div className="title-and-add-btn-row">
            <div className="pet-booking-titles">
              <Heading
                size="sm"
                description={
                  totalNewPets > 1
                    ? 'Enter basic details of your next pet'
                    : 'Enter basic details of your pet'
                }
                fontWeight="sm"
              />
            </div>
            {
              <div
                className="more-details"
                onClick={() => {
                  if (showMorePetDetails) {
                    setShowMorePetDetails(false);
                  } else {
                    setShowMorePetDetails(true);
                  }
                }}
              >
                {showMorePetDetails ? '- Less details' : '+ More details'}
              </div>
            }
          </div>
        ) : (
          <div className="add-new-title">Add new pet details</div>
        )}
        <div className="pet-row pet-type">
          <div className="pet-col-6">
            <PetType
              onChange={onPetTypeChange}
              value={pet?.petType}
              defaultPetType={defaultPetType}
            />
          </div>
        </div>
        {userContext && userContext?.isLandscape ? (
          <div className="title-and-add-btn-row">
            <div>
              <Heading
                size="sm"
                title=""
                description="Enter name and breed of your pet"
                fontWeight="sm"
              />
            </div>
          </div>
        ) : (
          <div className="add-new-title">Enter name and breed of your pet</div>
        )}
        <div className="pet-row mtop-2 mt-3">
          <div className="pet-col-4">
            <input
              type="text"
              className="new-cust-input"
              placeholder="Pet name"
              id="name"
              onChange={handleChangePetDetails}
              value={pet?.name}
              maxLength={20}
            />
          </div>
          <div className="pet-col-4 petsked-form">
            <DropDown
              placeholder="Select Breed"
              selectedValue={pet?.breed}
              data={breedList ? breedList : []}
              onSelect={(value: any) => {
                onBreedChange(value);
              }}
              onDismiss={() => {}}
              enableSearch={true}
            />
          </div>
        </div>
        <div className="booking-desc pet-size">Select your pet size</div>
        <div className="selected-pet-row">
          <PetWeightSelection
            selectedSize={undefined}
            onChange={onPetSizeChange}
            availableSizeConfiguration={availableSizeConfiguration}
          />
        </div>
        {userContext && !userContext?.isLandscape && (
          <div
            className="more-details mobile-more-details"
            onClick={() => {
              if (showMorePetDetails) {
                setShowMorePetDetails(false);
              } else {
                setShowMorePetDetails(true);
              }
            }}
          >
            {showMorePetDetails ? '' : '+ More details'}
          </div>
        )}
        {showMorePetDetails && (
          <>
            <div className="pet-edit-title mb-3">Add some extra pet details below</div>
            <div className="pet-row">
              <div className="pet-col-4">
                <DatePickerWithPortal
                  value={pet?.dateOfBirth}
                  maximumDate={day}
                  onChange={(val: DayValue) => {
                    onDOBChange(val);
                  }}
                  placeholder={t('Select date of birth')}
                />
              </div>
              <div className="pet-col-4 gender-selection">
                <div className="p-radio-group p-pl-30">
                  <label className="container-1">
                    <span>Male</span>
                    <input
                      type="radio"
                      name="radio"
                      onChange={() => onGenderChange('male')}
                      checked={pet?.gender?.toLowerCase() === 'male'}
                    />
                    <span className="checkmark"></span>
                  </label>
                  <div className="pet-details-divider"></div>
                  <label className="container-1">
                    <span>Female</span>
                    <input
                      type="radio"
                      name="radio"
                      onChange={() => onGenderChange('female')}
                      value={pet?.gender}
                      checked={pet?.gender?.toLowerCase() === 'female'}
                    />
                    <span className="checkmark"></span>
                  </label>
                </div>
              </div>
            </div>
            <div className="pet-row">
              <div className="pet-col-4">
                <DropDown
                  placeholder="Select Color"
                  selectedValue={pet?.petColor}
                  data={colorList ? colorList : []}
                  onSelect={(item: any) => {
                    onColorChange(item.value);
                  }}
                  onDismiss={() => {}}
                  enableSearch={true}
                  setObject={true}
                />
              </div>
              <div className="pet-col-4">
                <input
                  type="text"
                  className="new-cust-input"
                  placeholder="Pet age"
                  id="petAge"
                  onChange={handleChangePetDetails}
                  value={pet?.petAge}
                  maxLength={20}
                />
              </div>
            </div>

            <div className="pet-row">
              <div className="pet-col-8">
                <textarea
                  name="notes"
                  id="notes"
                  className="block w-full new-search-textarea "
                  placeholder="Add Note.."
                  value={pet?.notes}
                  rows={4}
                  onChange={handleChangePetDetails}
                />
              </div>
            </div>
          </>
        )}
      </>

      {userContext && !userContext?.isLandscape && showMorePetDetails && (
        <div
          className="more-details mobile-more-details"
          onClick={() => {
            if (showMorePetDetails) {
              setShowMorePetDetails(false);
            } else {
              setShowMorePetDetails(true);
            }
          }}
        >
          {showMorePetDetails ? '- Less details' : '+ More details'}
        </div>
      )}
      {!hideAddNewPet && selectedBookingView !== 'grooming' && (
        <>
          {userContext?.isLandscape && <div className="pet-divider"></div>}
          <div className="footer-action-petform">
            {data ? (
              <Button
                type="button"
                size="lg"
                variant="inverse"
                onClick={() => {
                  addNewPet();
                }}
              >
                Update Details
              </Button>
            ) : (
              <Button
                type="button"
                size="lg"
                variant="inverse"
                startIcon={<Servicesicon4 />}
                onClick={() => {
                  addNewPet();
                }}
              >
                Add Another Pet
              </Button>
            )}
            {data && (
              <Button
                type="button"
                size="lg"
                variant="nobg"
                startIcon={<DeleteIcon />}
                onClick={() => {
                  onDeleteNewPet();
                }}
              >
                Remove Pet
              </Button>
            )}
          </div>
        </>
      )}
    </div>
  ) : (
    <></>
  );
};
